/**
 * Weather Widget - Displays current weather conditions
 * Uses Open-Meteo API (free, no API key required)
 */

import type { Widget, WidgetConfig } from './index';

export interface WeatherConfig extends WidgetConfig {
  latitude?: number;
  longitude?: number;
  city?: string;
  unit?: 'celsius' | 'fahrenheit';
  showCondition?: boolean;
  showHumidity?: boolean;
  showWind?: boolean;
  refreshInterval?: number; // minutes
  fontSize?: number;
  color?: string;
  widgetBg?: string;
  widgetBgTransparent?: boolean;
  // Editor compat alias
  location?: string;
}

interface WeatherData {
  temperature: number;
  condition: string;
  conditionCode: number;
  humidity: number;
  windSpeed: number;
  icon: string;
}

export class WeatherWidget implements Widget {
  private container: HTMLElement;
  private config: WeatherConfig;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private weatherData: WeatherData | null = null;

  constructor(config: WeatherConfig = {}) {
    // Map editor property names to player config
    const mapped: Record<string, unknown> = { ...config };
    if ('location' in mapped && !('city' in mapped)) {
      mapped.city = mapped.location;
    }

    this.config = {
      latitude: 41.0082, // Istanbul default
      longitude: 28.9784,
      city: 'Istanbul',
      unit: 'celsius',
      showCondition: true,
      showHumidity: false,
      showWind: false,
      refreshInterval: 30,
      fontSize: 48,
      color: '#ffffff',
      ...mapped,
    };

    this.container = document.createElement('div');
    this.container.className = 'widget-weather';
  }

  render(): HTMLElement {
    const bg = this.config.widgetBgTransparent
      ? 'transparent'
      : (this.config.widgetBg || 'transparent');

    this.container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      color: ${this.config.color};
      background: ${bg};
    `;

    this.container.innerHTML = `
      <div style="font-size: 24px; opacity: 0.7;">Loading weather...</div>
    `;

    this.fetchWeather();
    this.intervalId = setInterval(
      () => this.fetchWeather(),
      (this.config.refreshInterval || 30) * 60 * 1000
    );

    return this.container;
  }

  update(): void {
    this.fetchWeather();
  }

  private async fetchWeather(): Promise<void> {
    try {
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${this.config.latitude}&longitude=${this.config.longitude}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&timezone=auto`;

      const response = await fetch(url);
      if (!response.ok) throw new Error('Weather API error');

      const data = await response.json();

      this.weatherData = {
        temperature: Math.round(data.current.temperature_2m),
        conditionCode: data.current.weather_code,
        condition: this.getConditionText(data.current.weather_code),
        humidity: data.current.relative_humidity_2m,
        windSpeed: Math.round(data.current.wind_speed_10m),
        icon: this.getWeatherIcon(data.current.weather_code),
      };

      this.updateDisplay();
    } catch (error) {
      console.error('[WeatherWidget] Failed to fetch weather:', error);
      this.container.innerHTML = `
        <div style="font-size: 24px; opacity: 0.5;">Weather unavailable</div>
      `;
    }
  }

  private updateDisplay(): void {
    if (!this.weatherData) return;

    const temp = this.config.unit === 'fahrenheit'
      ? Math.round(this.weatherData.temperature * 9/5 + 32)
      : this.weatherData.temperature;

    const unit = this.config.unit === 'fahrenheit' ? '°F' : '°C';

    let html = `
      <div style="font-size: ${Math.round(this.config.fontSize! * 1.2)}px; margin-bottom: 8px;">
        ${this.weatherData.icon}
      </div>
      <div style="font-size: ${this.config.fontSize}px; font-weight: bold;">
        ${temp}${unit}
      </div>
    `;

    if (this.config.showCondition) {
      html += `
        <div style="font-size: ${Math.round(this.config.fontSize! * 0.4)}px; opacity: 0.8; margin-top: 4px;">
          ${this.weatherData.condition}
        </div>
      `;
    }

    if (this.config.city) {
      html += `
        <div style="font-size: ${Math.round(this.config.fontSize! * 0.3)}px; opacity: 0.6; margin-top: 8px;">
          ${this.config.city}
        </div>
      `;
    }

    const details: string[] = [];
    if (this.config.showHumidity) {
      details.push(`💧 ${this.weatherData.humidity}%`);
    }
    if (this.config.showWind) {
      details.push(`💨 ${this.weatherData.windSpeed} km/h`);
    }

    if (details.length > 0) {
      html += `
        <div style="font-size: ${Math.round(this.config.fontSize! * 0.3)}px; opacity: 0.6; margin-top: 8px;">
          ${details.join('  ')}
        </div>
      `;
    }

    this.container.innerHTML = html;
  }

  private getWeatherIcon(code: number): string {
    // WMO Weather interpretation codes
    if (code === 0) return '☀️'; // Clear sky
    if (code <= 3) return '⛅'; // Partly cloudy
    if (code <= 49) return '🌫️'; // Fog
    if (code <= 59) return '🌧️'; // Drizzle
    if (code <= 69) return '🌧️'; // Rain
    if (code <= 79) return '🌨️'; // Snow
    if (code <= 84) return '🌧️'; // Rain showers
    if (code <= 86) return '🌨️'; // Snow showers
    if (code <= 99) return '⛈️'; // Thunderstorm
    return '🌡️';
  }

  private getConditionText(code: number): string {
    if (code === 0) return 'Clear';
    if (code <= 3) return 'Partly Cloudy';
    if (code <= 49) return 'Foggy';
    if (code <= 59) return 'Drizzle';
    if (code <= 69) return 'Rainy';
    if (code <= 79) return 'Snowy';
    if (code <= 84) return 'Showers';
    if (code <= 86) return 'Snow Showers';
    if (code <= 99) return 'Thunderstorm';
    return 'Unknown';
  }

  destroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
